import { ActivityFormContext } from '@/contexts/ActivityFormContext';
import { Lightbulb, SmartToy } from '@mui/icons-material';
import { Badge, Box, IconButton, Typography, useTheme } from '@mui/material';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssistantSuggestionPopper from './AssistantSuggestionPopper';
import AiLegalDialog from '../utils/dialogs/AiLegalDialog';

type AssistantButtonProps = {
  sx?: React.CSSProperties;
};

const AssistantButton = (props: AssistantButtonProps) => {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const {
    assistantAnchorRef,
    assistantAnchorEl,
    setAssistantAnchorEl,
    showAssistantBadge,
    setShowAssistantBadge,
  } = useContext(ActivityFormContext);
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);
  const aiEnabled = localStorage.getItem('aiEnabled') === 'true';

  const onAssistantClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (assistantAnchorEl && setAssistantAnchorEl) {
      setAssistantAnchorEl(null);
      return;
    }

    if (assistantAnchorRef.current && setAssistantAnchorEl) {
      setAssistantAnchorEl(assistantAnchorRef.current);
      setShowAssistantBadge?.(false);
    }
  };

  const enableAI = () => {
    localStorage.setItem('aiEnabled', 'true');
    window.dispatchEvent(new Event('storage'));
    setLegalDialogOpen(false);
  };

  return (
    <>
      <IconButton
        ref={assistantAnchorRef}
        onClick={onAssistantClick}
        aria-label={`${t('AI Assistant')}`}
        sx={{ position: 'absolute', right: 0, padding: 1, mx: 2, bgcolor: 'line' }}
      >
        {showAssistantBadge ? (
          <Badge
            badgeContent={
              <Box
                sx={{
                  bgcolor: palette.mode === 'dark' ? palette.primary.main : palette.primary.light,
                  borderRadius: '50%',
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Lightbulb
                  sx={{
                    fontSize: 14,
                    color: palette.grey[50],
                  }}
                />
              </Box>
            }
            invisible={!aiEnabled}
            overlap="circular"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <SmartToy
              sx={{
                fontSize: 30,
                color: palette.mode === 'dark' ? palette.grey[300] : palette.primary.light,
              }}
            />
          </Badge>
        ) : (
          <SmartToy
            sx={{
              fontSize: 30,
              color: palette.mode === 'dark' ? palette.grey[300] : palette.primary.light,
            }}
          />
        )}
      </IconButton>
      {!aiEnabled && (
        <AssistantSuggestionPopper
          anchorEl={assistantAnchorEl ?? null}
          open={!!assistantAnchorEl}
          loading={false}
          onClose={() => {
            setAssistantAnchorEl?.(null);
          }}
          onAccept={() => {
            setLegalDialogOpen(true);
            setAssistantAnchorEl?.(null);
          }}
          activateAi={true}
        >
          <Typography variant="body2" color={palette.grey[50]}>
            {t(
              "Hello! I'm an AI assistant that can help you with your activities. To use me, please activate AI features by clicking the button below.",
            )}
          </Typography>
        </AssistantSuggestionPopper>
      )}
      <AiLegalDialog
        open={legalDialogOpen}
        onEnable={enableAI}
        onClose={() => setLegalDialogOpen(false)}
      />
    </>
  );
};

export default AssistantButton;
